import { BatteryChemistry, type DischargeCurvePoint } from '@brt/shared-types'
import { getChemistryPreset } from '../data/chemistryPresets.js'
import { roundTo4 } from '../utils/rounding.js'

export function getVoltageAtSoC(
  chemistry: BatteryChemistry,
  socPercent: number,
  nominalVoltage: number,
  cutoffVoltage: number,
): number {
  const soc = Math.max(0, Math.min(100, socPercent)) / 100
  const range = nominalVoltage - cutoffVoltage

  if (soc <= 0) return cutoffVoltage
  if (soc >= 1) return nominalVoltage + range * 0.04

  switch (chemistry) {
    case BatteryChemistry.LI_FEPO4: {
      const delta = nominalVoltage - cutoffVoltage
      const fullyCharged = nominalVoltage + delta * 0.05
      const upperKnee = 0.95
      const lowerKnee = 0.12
      const flatVoltage = nominalVoltage + delta * 0.01

      if (soc > upperKnee) {
        const t = (soc - upperKnee) / (1 - upperKnee)
        return flatVoltage + (fullyCharged - flatVoltage) * Math.pow(t, 0.6)
      }
      if (soc > lowerKnee) {
        const t = (soc - lowerKnee) / (upperKnee - lowerKnee)
        return flatVoltage - (flatVoltage - cutoffVoltage) * 0.02 * (1 - t)
      }
      const t = soc / lowerKnee
      return cutoffVoltage + (flatVoltage - cutoffVoltage) * Math.pow(t, 2.5)
    }

    case BatteryChemistry.LI_ION: {
      const delta = nominalVoltage - cutoffVoltage
      const fullyCharged = nominalVoltage + delta * 0.08
      if (soc > 0.9) {
        const t = (soc - 0.9) / 0.1
        return nominalVoltage + (fullyCharged - nominalVoltage) * Math.pow(t, 0.7)
      }
      return cutoffVoltage + range * Math.pow(soc, 0.85)
    }

    case BatteryChemistry.LEAD_ACID_FLOODED:
    case BatteryChemistry.AGM:
    case BatteryChemistry.GEL: {
      const delta = nominalVoltage - cutoffVoltage
      const fullyCharged = nominalVoltage + delta * 0.12
      if (soc > 0.9) {
        const t = (soc - 0.9) / 0.1
        return nominalVoltage + (fullyCharged - nominalVoltage) * Math.pow(t, 0.5)
      }
      return cutoffVoltage + range * soc
    }

    case BatteryChemistry.NI_MH:
    case BatteryChemistry.NI_CD: {
      const delta = nominalVoltage - cutoffVoltage
      const fullyCharged = nominalVoltage + delta * 0.2
      const peakVoltage = nominalVoltage + delta * 0.05
      if (soc > 0.9) {
        const t = (soc - 0.9) / 0.1
        return peakVoltage + (fullyCharged - peakVoltage) * t
      }
      if (soc > 0.8) {
        const t = (soc - 0.8) / 0.1
        return nominalVoltage + (peakVoltage - nominalVoltage) * t
      }
      if (soc > 0.15) {
        return nominalVoltage - delta * 0.02 * (1 - (soc - 0.15) / 0.65)
      }
      const t = soc / 0.15
      return cutoffVoltage + (nominalVoltage - cutoffVoltage) * Math.pow(t, 1.5)
    }

    case BatteryChemistry.ALKALINE: {
      const fullyCharged = nominalVoltage + range * 0.08
      if (soc > 0.95) {
        const t = (soc - 0.95) / 0.05
        return nominalVoltage + (fullyCharged - nominalVoltage) * t
      }
      return cutoffVoltage + range * Math.pow(soc, 0.6)
    }

    default:
      return cutoffVoltage + range * soc
  }
}

function detectKneePoint(points: DischargeCurvePoint[]): number {
  if (points.length < 3) return -1

  let maxSlope = 0
  let maxSlopeIdx = -1
  for (let i = 1; i < points.length; i++) {
    const dV = Math.abs(points[i]!.voltage - points[i - 1]!.voltage)
    if (dV > maxSlope) {
      maxSlope = dV
      maxSlopeIdx = i
    }
  }

  let maxSecondDeriv = 0
  let kneeIdx = maxSlopeIdx >= 0 ? maxSlopeIdx : -1

  for (let i = 1; i < points.length - 1; i++) {
    const d2V = Math.abs(
      points[i + 1]!.voltage - 2 * points[i]!.voltage + points[i - 1]!.voltage,
    )
    if (d2V > maxSecondDeriv) {
      maxSecondDeriv = d2V
      kneeIdx = i
    }
  }

  return kneeIdx
}

export function generateDischargeCurve(
  chemistry: BatteryChemistry,
  effectiveCapacityAh: number,
  loadA: number,
  steps: number = 100,
  cellVoltage?: number,
): DischargeCurvePoint[] {
  const preset = getChemistryPreset(chemistry)
  const nominalVoltage = cellVoltage ?? preset.nominalCellVoltage
  const cutoffVoltage = preset.cutoffVoltage

  if (effectiveCapacityAh <= 0 || loadA <= 0) {
    return []
  }

  const totalRuntimeHours = effectiveCapacityAh / loadA
  const stepSize = totalRuntimeHours / steps

  const points: DischargeCurvePoint[] = []

  for (let i = 0; i <= steps; i++) {
    const timeHours = roundTo4(stepSize * i)
    const socPercent = roundTo4(100 * (1 - i / steps))
    const voltage = roundTo4(getVoltageAtSoC(chemistry, socPercent, nominalVoltage, cutoffVoltage))

    points.push({
      timeHours,
      socPercent,
      voltage,
      isKneePoint: false,
    })
  }

  const kneeIdx = detectKneePoint(points)
  if (kneeIdx >= 0 && kneeIdx < points.length) {
    points[kneeIdx]!.isKneePoint = true
  }

  return points
}

export interface VoltageCutoffRuntime {
  runtimeHours: number
  socPercent: number
}

export function findVoltageCutoffRuntime(
  curve: DischargeCurvePoint[],
  minVoltage: number,
): VoltageCutoffRuntime | null {
  for (const point of curve) {
    if (point.voltage <= minVoltage) {
      return { runtimeHours: point.timeHours, socPercent: point.socPercent }
    }
  }
  return null
}
